import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../components/layout/Navigation';
import { Footer } from '../../components/layout/Footer';
import { Container } from '../../components/ui/Container';

const LAST_UPDATED = 'March 2, 2026';

export const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-brand-dark text-white">
      <Navigation />

      <main className="pt-28 pb-16 md:pt-32">
        <Container className="max-w-4xl">
          <section className="rounded-2xl border border-brand-gray-700 bg-brand-gray-900/70 p-7 md:p-10">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-gold">Legal</p>
            <h1 className="mt-3 text-4xl font-bold md:text-5xl">Terms of Service</h1>
            <p className="mt-3 text-sm text-gray-300">Last updated: {LAST_UPDATED}</p>
            <p className="mt-6 text-gray-200">
              These Terms of Service ("Terms") govern your access to and use of Master Polyglots,
              including our website, courses, and related services. By using our services, you
              agree to these Terms.
            </p>

            <div className="mt-8 space-y-8">
              <section>
                <h2 className="text-2xl font-semibold">1. Eligibility and Accounts</h2>
                <p className="mt-3 text-gray-300">
                  You must be legally able to enter into a binding agreement to use our services.
                  You are responsible for maintaining the confidentiality of your account and for
                  all activity occurring under your account.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">2. Purchases and Access</h2>
                <p className="mt-3 text-gray-300">
                  Course purchases grant you the access rights described on the course page at the
                  time of purchase. Access may be delivered through direct course unlocks, purchase
                  verification, or access-code redemption workflows.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">3. Pricing and Payments</h2>
                <p className="mt-3 text-gray-300">
                  Prices are listed in the applicable checkout flow and may change over time.
                  Payments are processed by third-party providers, including Stripe, and are subject
                  to their terms. You agree to provide valid payment information and authorize
                  charges for selected purchases.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">4. Acceptable Use</h2>
                <p className="mt-3 text-gray-300">
                  You agree not to misuse the platform, interfere with service operations, bypass
                  access controls, scrape protected content, or use our services for unlawful,
                  harmful, or abusive activity.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">5. Intellectual Property</h2>
                <p className="mt-3 text-gray-300">
                  All platform content, including videos, lessons, curriculum, branding, and
                  downloadable materials, is owned by or licensed to Master Polyglots and protected
                  by applicable intellectual property laws. You receive a limited, non-transferable
                  license for personal educational use only.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">6. Downloads and Materials</h2>
                <p className="mt-3 text-gray-300">
                  Downloadable files are provided for enrolled students according to course access
                  rules. You may not redistribute, resell, publicly host, or otherwise exploit
                  downloaded materials outside your personal learning use.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">7. Suspension or Termination</h2>
                <p className="mt-3 text-gray-300">
                  We may suspend or terminate access where needed to protect users, enforce these
                  Terms, or comply with legal obligations. Sections that should reasonably survive
                  termination will remain in effect.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">8. Disclaimers</h2>
                <p className="mt-3 text-gray-300">
                  Services are provided on an "as is" and "as available" basis. We do not guarantee
                  uninterrupted operation, specific employment outcomes, or that content will meet
                  every individual requirement.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">9. Limitation of Liability</h2>
                <p className="mt-3 text-gray-300">
                  To the maximum extent permitted by law, Master Polyglots is not liable for
                  indirect, incidental, special, consequential, or punitive damages arising from
                  your use of the services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">10. Changes to These Terms</h2>
                <p className="mt-3 text-gray-300">
                  We may revise these Terms periodically. Updated Terms become effective when posted,
                  and continued use of services after updates constitutes acceptance.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">11. Contact</h2>
                <p className="mt-3 text-gray-300">
                  For questions regarding these Terms, contact
                  {' '}
                  <a href="mailto:clintonjohnson@cmjsoftware.com" className="text-brand-gold underline">
                    clintonjohnson@cmjsoftware.com
                  </a>
                  .
                </p>
              </section>
            </div>

            <p className="mt-10 text-sm text-gray-400">
              Please also review our
              {' '}
              <Link to="/privacy" className="text-brand-gold underline">
                Privacy Policy
              </Link>
              .
            </p>
          </section>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

